/**
 * charts - theme-aware wrapper over ApexCharts, entry point.
 *
 * Usage:
 *   const c = g.chart('#sales', { type: 'area', series: [...], ... });
 *   c.setSeries(newSeries);           // swap the data
 *   c.update({ chart: { height: 420 } });  // merge new options
 *   c.destroy();                      // tear down (also g.chart.get('#sales'))
 *
 * The page writes only data + intent; colors, fonts, axis/grid and the tooltip
 * are read from the live CSS theme tokens and RE-APPLIED automatically when the
 * app theme flips (data-bs-theme on <html>). The palette comes from the theme
 * colors (--bs-primary, success, ...) so per-app distinctiveness lives in the
 * SCSS variables, like everywhere else.
 *
 * Requires the ApexCharts vendor, which loads PER PAGE: add
 * `'vendors' => ['apexcharts'], 'libs' => ['charts']` to the page's route.php.
 * Full reference: docs/libs/charts.md.
 */
import { Chart, refreshAllThemes, setReduceMotion } from './chart.js';

// Bump on every meaningful change to the library.
const VERSION = '0.1.0';

function chart(target, config) {
	const el = typeof target === 'string' ? document.querySelector(target) : target;
	if (!el) return null;
	if (typeof ApexCharts === 'undefined') {
		console.error("[charts] ApexCharts vendor not loaded on this page - add 'vendors' => ['apexcharts'] in the page's route.php");
		return null;
	}
	if (el.genusChart) el.genusChart.destroy();   // re-init replaces the old chart
	return new Chart(el, config || {});
}

chart.version = () => VERSION;
chart.get = (target) => {
	const el = typeof target === 'string' ? document.querySelector(target) : target;
	return (el && el.genusChart) || null;
};

// Re-theme every live chart when the app theme flips, and honor reduced motion.
function initThemeWatch() {
	const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
	setReduceMotion(mq.matches);
	mq.addEventListener ? mq.addEventListener('change', (e) => setReduceMotion(e.matches)) : mq.addListener((e) => setReduceMotion(e.matches));

	new MutationObserver((mutations) => {
		if (mutations.some((m) => m.attributeName === 'data-bs-theme')) refreshAllThemes();
	}).observe(document.documentElement, { attributes: true, attributeFilter: ['data-bs-theme'] });
}

if (window.g) {
	window.g.chart = chart;
	window.g.registerLib('charts');
	initThemeWatch();
} else {
	window.chart = chart;
}
